import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap";
import axios from "axios";
import moment from "moment";
import { END_POINT } from "../../../constants/defaultValues";
import { Separator } from "../../../components/common/CustomBootstrap";
import FooterPage from "../../../containers/pages/FooterPage";
import IntlMessages from "../../../helpers/IntlMessages";
import { injectIntl } from "react-intl";

const TYPES = [
  { value: "internal_issue", label: "Xuất sử dụng nội bộ" },
  { value: "supplier_return", label: "Trả nhà cung cấp" },
  { value: "disposal", label: "Tiêu hủy" },
  { value: "other", label: "Xuất khác" },
];

const STATUS = {
  draft: { label: "Nháp", color: "secondary" },
  pending_approval: { label: "Chờ duyệt", color: "warning" },
  posted: { label: "Đã xuất", color: "success" },
  rejected: { label: "Từ chối", color: "danger" },
  reversed: { label: "Đã hoàn tác", color: "dark" },
};

const newRequestId = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    return (char === "x" ? random : (random & 0x3) | 0x8).toString(16);
  });
};

const formatQuantity = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";
  return Number(number.toFixed(2)).toString();
};

const numberValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value || "").trim();
  const normalized = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(raw)
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw.replace(",", ".");
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

const StockOutPage = () => {
  const [documents, setDocuments] = useState([]);
  const [kpis, setKpis] = useState({});
  const [stocks, setStocks] = useState([]);
  const [providers, setProviders] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isFormOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("info");
  const [isDetailMobile, setDetailMobile] = useState(() => (
    typeof window !== "undefined" ? window.innerWidth < 992 : false
  ));
  const [form, setForm] = useState({
    type: "internal_issue",
    provider_id: "",
    reason_code: "",
    notes: "",
    transaction_date: moment().format("YYYY-MM-DD"),
    items: [],
  });

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("access_token")}` }),
    [],
  );

  const loadDetailById = async (id, showAlert = true) => {
    try {
      const response = await axios.get(`${END_POINT}/api/stock-outs/${id}`, { headers });
      setDetailTab("info");
      setDetail(response.data.data || null);
    } catch (error) {
      if (showAlert) window.alert(error.response?.data?.responseText || "Không thể tải chi tiết phiếu");
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${END_POINT}/api/stock-outs`, {
        headers,
        params: { type: typeFilter || undefined, status: statusFilter || undefined },
      });
      const rows = response.data.data || [];
      setDocuments(rows);
      setKpis(response.data.kpis || {});
      const hasCurrentDetail = detail && rows.some((row) => String(row.id) === String(detail.id));
      if ((!detail || !hasCurrentDetail) && rows[0]?.id) await loadDetailById(rows[0].id, false);
      if (detail && !hasCurrentDetail && !rows[0]) setDetail(null);
    } catch (error) {
      console.error("Error loading stock-out documents:", error);
    }
  };

  const loadReferenceData = async () => {
    try {
      const [stockResponse, providerResponse] = await Promise.all([
        axios.get(`${END_POINT}/stock/all-unit`, { headers }),
        axios.get(`${END_POINT}/company-provider`, { headers }),
      ]);
      setStocks(stockResponse.data.data || []);
      setProviders(providerResponse.data.data || []);
    } catch (error) {
      console.error("Error loading stock-out reference data:", error);
    }
  };

  useEffect(() => { loadReferenceData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadDocuments(); }, [typeFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const syncDetailMode = () => setDetailMobile(window.innerWidth < 992);
    syncDetailMode();
    window.addEventListener("resize", syncDetailMode);
    return () => window.removeEventListener("resize", syncDetailMode);
  }, []);

  const resetForm = () => setForm({
    type: "internal_issue",
    provider_id: "",
    reason_code: "",
    notes: "",
    transaction_date: moment().format("YYYY-MM-DD"),
    items: [],
  });

  const addItem = (stockId) => {
    const stock = stocks.find((item) => String(item.id) === String(stockId));
    if (!stock || form.items.some((item) => String(item.stock_id) === String(stock.id))) return;
    setForm({ ...form, items: [...form.items, { stock_id: stock.id, quantity: 1, reason: "" }] });
  };

  const updateItem = (stockId, field, value) => setForm({
    ...form,
    items: form.items.map((item) => String(item.stock_id) === String(stockId) ? { ...item, [field]: value } : item),
  });

  const save = async (submitAfterSave) => {
    try {
      const response = await axios.post(`${END_POINT}/api/stock-outs`, {
        ...form,
        request_id: newRequestId(),
        items: form.items.map((item) => ({ ...item, quantity: Number(item.quantity) })),
      }, { headers });
      const document = response.data.data;
      if (submitAfterSave && document?.status === "draft") {
        await axios.post(`${END_POINT}/api/stock-outs/${document.id}/submit`, {}, { headers });
      }
      setFormOpen(false);
      resetForm();
      loadDocuments();
    } catch (error) {
      window.alert(error.response?.data?.responseText || "Không thể lưu phiếu xuất kho");
    }
  };

  const action = async (document, actionName) => {
    let reason = "";
    if (actionName === "reject" || actionName === "reverse") {
      reason = window.prompt(actionName === "reject" ? "Lý do từ chối" : "Lý do hoàn tác");
      if (!reason) return;
    }
    try {
      await axios.post(`${END_POINT}/api/stock-outs/${document.id}/${actionName}`, reason ? { reason } : {}, { headers });
      loadDocuments();
      if (detail?.id === document.id) viewDetail(document.id);
    } catch (error) {
      window.alert(error.response?.data?.responseText || "Không thể thực hiện thao tác");
    }
  };

  const removeDraft = async (document) => {
    if (!window.confirm("Xóa phiếu nháp này?")) return;
    try {
      await axios.delete(`${END_POINT}/api/stock-outs/${document.id}`, { headers });
      loadDocuments();
      if (detail?.id === document.id) setDetail(null);
    } catch (error) {
      window.alert(error.response?.data?.responseText || "Không thể xóa phiếu");
    }
  };

  const viewDetail = (id) => loadDetailById(id, true);

  const amount = (value) => new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(Number(value || 0));
  const selectedProvider = providers.find((provider) => String(provider.id) === String(form.provider_id));
  const providerStockItem = (stockId) => (selectedProvider?.company_provider_item || []).find(
    (item) => String(item.stock_id) === String(stockId),
  );
  const lineUnitCost = (stock) => {
    if (form.type === "supplier_return" && selectedProvider) {
      const providerItem = providerStockItem(stock.id);
      const providerQuantity = numberValue(providerItem?.quantity);
      if (providerQuantity > 0) return numberValue(providerItem.price_income) / providerQuantity;
    }
    return numberValue(stock.avarage_price);
  };
  const lineValue = (item, stock) => numberValue(item.quantity) * lineUnitCost(stock);
  const typeLabel = (value) => (TYPES.find((item) => item.value === value) || {}).label || value;
  const kpiCards = [
    {
      key: "totalExportValue",
      label: "Tổng giá trị xuất",
      value: amount(kpis.totalExportValue),
      note: `${kpis.postedCount || 0} phiếu đã xuất`,
    },
    {
      key: "pendingApprovalCount",
      label: "Chờ duyệt",
      value: kpis.pendingApprovalCount || 0,
      note: "phiếu cần xử lý",
    },
    {
      key: "postedCount",
      label: "Đã xuất",
      value: kpis.postedCount || 0,
      note: amount(kpis.postedValue),
    },
    {
      key: "disposalCount",
      label: "Tiêu hủy",
      value: kpis.disposalCount || 0,
      note: amount(kpis.disposalValue),
    },
    {
      key: "reversedCount",
      label: "Đã hoàn tác",
      value: kpis.reversedCount || 0,
      note: amount(kpis.reversedValue),
    },
  ];
  const detailTabs = [
    { key: "info", label: "Thông tin" },
    { key: "products", label: "Sản phẩm" },
    { key: "approval", label: "Phê duyệt" },
    { key: "logs", label: "Nhật ký" },
  ];
  const dateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");
  const renderDetailActions = (document) => {
    if (!document) return null;
    if (document.status === "draft") {
      return (
        <>
          <Button size="xs" color="primary" onClick={() => action(document, "submit")}>Gửi duyệt</Button>
          <Button size="xs" color="danger" outline onClick={() => removeDraft(document)}>Xóa</Button>
        </>
      );
    }
    if (document.status === "pending_approval") {
      return (
        <>
          <Button size="xs" color="success" onClick={() => action(document, "approve")}>Duyệt</Button>
          <Button size="xs" color="danger" outline onClick={() => action(document, "reject")}>Từ chối</Button>
        </>
      );
    }
    if (document.status === "posted") {
      return <Button size="xs" color="danger" outline onClick={() => action(document, "reverse")}>Hoàn tác</Button>;
    }
    return null;
  };
  const DetailField = ({ label, value, right }) => (
    <div className="stock-out-detail-field">
      <span>{label}</span>
      <strong className={right ? "text-right" : ""}>{value === undefined || value === null || value === "" ? "-" : value}</strong>
    </div>
  );
  const renderDetailTab = () => {
    if (!detail) return null;
    if (detailTab === "products") {
      return (
        <Table responsive size="sm" className="mb-0">
          <thead><tr><th>Hàng hóa</th><th className="text-right">SL</th><th className="text-right">Giá vốn</th><th className="text-right">Thành tiền</th></tr></thead>
          <tbody>{(detail.items || []).map((item) => <tr key={item.id}><td>{item.stockNameSnapshot}<br /><small>{item.unitSnapshot}</small></td><td className="text-right">{formatQuantity(item.quantity)}</td><td className="text-right">{amount(item.unitCost)}</td><td className="text-right">{amount(item.lineTotal)}</td></tr>)}</tbody>
        </Table>
      );
    }
    if (detailTab === "approval") {
      return (
        <div>
          <DetailField label="Người lập" value={detail.createdByName} />
          <DetailField label="Gửi duyệt" value={`${detail.submittedByName || "-"} · ${dateTime(detail.submittedAt)}`} />
          <DetailField label="Người duyệt" value={`${detail.approvedByName || "-"} · ${dateTime(detail.approvedAt)}`} />
          <DetailField label="Từ chối" value={`${detail.rejectedByName || "-"} · ${dateTime(detail.rejectedAt)}`} />
          {detail.rejectionReason && <DetailField label="Lý do từ chối" value={detail.rejectionReason} />}
          <DetailField label="Hoàn tác" value={`${detail.reversedByName || "-"} · ${dateTime(detail.reversedAt)}`} />
          {detail.reversalReason && <DetailField label="Lý do hoàn tác" value={detail.reversalReason} />}
        </div>
      );
    }
    if (detailTab === "logs") {
      return (
        <div className="stock-out-detail-log">
          {(detail.events || []).length ? (detail.events || []).map((item) => (
            <div className="stock-out-detail-log-item" key={item.id}>
              <strong>{item.eventType}</strong>
              <span>{dateTime(item.createdAt)}{item.fromStatus || item.toStatus ? ` · ${item.fromStatus || "-"} → ${item.toStatus || "-"}` : ""}</span>
              {item.reason && <p>{item.reason}</p>}
            </div>
          )) : <p className="text-muted mb-0">Chưa có nhật ký.</p>}
        </div>
      );
    }
    return (
      <div className="stock-out-detail-info">
        <DetailField label="Mã phiếu" value={detail.code} />
        <DetailField label="Loại phiếu" value={typeLabel(detail.type)} />
        <DetailField label="Ngày xuất" value={moment(detail.transactionDate).format("DD/MM/YYYY")} />
        <DetailField label="Trạng thái" value={(STATUS[detail.status] || {}).label} />
        <DetailField label="Kho" value={detail.warehouseName} />
        <DetailField label="Nhà cung cấp" value={detail.provider?.name || "-"} />
        <DetailField label="Số mặt hàng" value={(detail.items || []).length} right />
        <DetailField label="Tổng số lượng" value={formatQuantity(detail.totalQuantity)} right />
        <DetailField label="Giá trị" value={amount(detail.totalCostValue)} right />
        <DetailField label="Người lập" value={detail.createdByName} />
        <DetailField label="Mục đích / lý do" value={detail.reasonCode || "-"} />
        <DetailField label="Ghi chú" value={detail.notes || "-"} />
      </div>
    );
  };
  const detailContent = detail && (
    <div className="stock-out-detail-content">
      <div className="stock-out-detail-header">
        <div>
          <p className="text-muted mb-1">Chi tiết phiếu</p>
          <h4 className="mb-1">{detail.code}</h4>
          <Badge color={(STATUS[detail.status] || {}).color}>{(STATUS[detail.status] || {}).label}</Badge>
        </div>
        <div className="stock-out-detail-header-actions">
          <div className="stock-out-detail-actions">{renderDetailActions(detail)}</div>
          <Button close onClick={() => setDetail(null)} className="d-lg-none" />
        </div>
      </div>
      <div className="stock-out-detail-summary">
        <div><span>Loại</span><strong>{typeLabel(detail.type)}</strong></div>
        <div><span>Ngày</span><strong>{moment(detail.transactionDate).format("DD/MM/YYYY")}</strong></div>
        <div><span>Giá trị</span><strong>{amount(detail.totalCostValue)}</strong></div>
      </div>
      <Nav tabs className="stock-out-detail-tabs">
        {detailTabs.map((item) => (
          <NavItem key={item.key}>
            <NavLink className={detailTab === item.key ? "active" : ""} onClick={() => setDetailTab(item.key)}>{item.label}</NavLink>
          </NavItem>
        ))}
      </Nav>
      <div className="stock-out-detail-body">{renderDetailTab()}</div>
    </div>
  );
  const emptyDetailContent = (
    <div className="stock-out-detail-content stock-out-detail-empty">
      <div className="stock-out-detail-header">
        <div>
          <p className="text-muted mb-1">Chi tiết phiếu</p>
          <h4 className="mb-1">Chưa chọn phiếu</h4>
          <Badge color="light">Sẵn sàng</Badge>
        </div>
      </div>
      <div className="stock-out-detail-body">
        <div className="stock-out-detail-empty-state">
          <i className="simple-icon-docs" />
          <h5>Chọn một phiếu để xem chi tiết</h5>
          <p>Panel sẽ tự mở phiếu đầu tiên khi danh sách có dữ liệu. Nếu chưa có phiếu, bạn có thể tạo phiếu xuất kho mới từ nút phía trên.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="disable-text-selection stock-out-page stock-out-page-with-panel">
      <div className="container-fluid">
        <div className="stock-out-desktop-layout">
          <div className="stock-out-main-content">
        <Row className="mb-4 stock-out-kpi-row">
          {kpiCards.map((item) => <Col sm="6" xs="12" className="stock-out-kpi-col" key={item.key}><Card><CardBody><p className="text-muted mb-1">{item.label}</p><h4 className="mb-0">{item.value}</h4><small>{item.note}</small></CardBody></Card></Col>)}
        </Row>

        <Card><CardBody>
          <Row className="mb-3">
            <Col md="4" className="mb-2 mb-md-0"><Input type="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="">Tất cả loại phiếu</option>{TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</Input></Col>
            <Col md="4" className="mb-2 mb-md-0"><Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="">Tất cả trạng thái</option>{Object.keys(STATUS).map((key) => <option key={key} value={key}>{STATUS[key].label}</option>)}</Input></Col>
            <Col md="4" className="text-md-right"><Button color="primary" onClick={() => setFormOpen(true)}><i className="simple-icon-plus mr-2" /><IntlMessages id="stock-out.create" /></Button></Col>
          </Row>
          <Table responsive hover>
            <thead><tr><th>Mã phiếu</th><th>Ngày</th><th>Loại</th><th>Kho</th><th>NCC</th><th className="text-right">Số mặt hàng</th><th className="text-right">Tổng số lượng</th><th className="text-right">Giá trị</th><th>Người lập</th><th>Trạng thái</th></tr></thead>
            <tbody>{documents.map((document) => <tr key={document.id} className="stock-out-document-row" onClick={() => viewDetail(document.id)}><td>{document.code}</td><td>{moment(document.transactionDate).format("DD/MM/YYYY")}</td><td>{typeLabel(document.type)}</td><td>{document.warehouseName}</td><td>{document.provider?.name || "-"}</td><td className="text-right">{document.itemCount || (document.items || []).length || 0}</td><td className="text-right">{formatQuantity(document.totalQuantity)}</td><td className="text-right">{amount(document.totalCostValue)}</td><td>{document.createdByName || "-"}</td><td><Badge color={(STATUS[document.status] || {}).color}>{(STATUS[document.status] || {}).label}</Badge></td></tr>)}</tbody>
          </Table>
        </CardBody></Card>
          </div>

          {!isDetailMobile && (
            <aside className="stock-out-detail-panel">
              {detailContent || emptyDetailContent}
            </aside>
          )}
        </div>
      </div>

      <Modal isOpen={isFormOpen} toggle={() => setFormOpen(false)} size="lg">
        <ModalHeader toggle={() => setFormOpen(false)}>Tạo phiếu xuất kho</ModalHeader>
        <ModalBody>
          <Row><Col md="4"><FormGroup><Label>Loại phiếu</Label><Input type="select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, provider_id: "", reason_code: "" })}>{TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</Input></FormGroup></Col><Col md="4"><FormGroup><Label>Ngày xuất</Label><Input type="date" value={form.transaction_date} onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} /></FormGroup></Col>{form.type === "supplier_return" && <Col md="4"><FormGroup><Label>Nhà cung cấp</Label><Input type="select" value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}><option value="">Chọn NCC</option>{providers.map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}</Input></FormGroup></Col>}</Row>
          <FormGroup><Label>{form.type === "disposal" ? "Lý do tiêu hủy" : "Mục đích / lý do"}</Label>{form.type === "disposal" ? <Input type="select" value={form.reason_code} onChange={(e) => setForm({ ...form, reason_code: e.target.value })}><option value="">Chọn lý do</option><option value="expired">Hết hạn</option><option value="damaged">Hư hỏng</option><option value="lost">Thất lạc</option><option value="quality_failure">Lỗi chất lượng</option><option value="other">Khác</option></Input> : <Input value={form.reason_code} onChange={(e) => setForm({ ...form, reason_code: e.target.value })} placeholder="Ví dụ: xuất dùng cho bếp, trả hàng lỗi..." />}</FormGroup>
          <FormGroup><Label>Ghi chú</Label><Input type="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormGroup>
          <FormGroup><Label>Thêm hàng hóa</Label><Input type="select" value="" onChange={(e) => addItem(e.target.value)}><option value="">Chọn hàng hóa</option>{stocks.map((stock) => <option key={stock.id} value={stock.id}>{stock.name} — tồn {formatQuantity(stock.count)} {stock.unit}</option>)}</Input></FormGroup>
          <Table responsive size="sm"><thead><tr><th>Hàng hóa</th><th>Tồn</th><th width="150">Số lượng xuất</th><th className="text-right">Giá trị</th><th /></tr></thead><tbody>{form.items.map((item) => { const stock = stocks.find((row) => String(row.id) === String(item.stock_id)) || {}; return <tr key={item.stock_id}><td>{stock.name}<br /><small className="text-muted">{stock.unit}</small></td><td>{formatQuantity(stock.count)}</td><td><Input type="number" min="0.001" step="0.001" value={item.quantity} onChange={(e) => updateItem(item.stock_id, "quantity", e.target.value)} /></td><td className="text-right">{amount(lineValue(item, stock))}</td><td><Button close onClick={() => setForm({ ...form, items: form.items.filter((row) => row.stock_id !== item.stock_id) })} /></td></tr>; })}</tbody></Table>
        </ModalBody>
        <ModalFooter><Button color="secondary" onClick={() => save(false)}>Lưu nháp</Button><Button color="primary" onClick={() => save(true)}>Lưu và xử lý phiếu</Button></ModalFooter>
      </Modal>

      <Modal isOpen={!!detail && isDetailMobile} toggle={() => setDetail(null)} size="lg" className="stock-out-detail-mobile-modal">
        <ModalBody>{detailContent}</ModalBody>
      </Modal>
      <Separator /><FooterPage />
    </div>
  );
};

export default injectIntl(StockOutPage);
